import {
    call,
    delay,
    getContext,
    put,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import cartApi from '@elab/core/store/cart/api';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import validation from '@elab/core/utils/validations';
import uiActions from '@elab/core/store/ui/actions';
import userSelectors from '@elab/core/store/user/selectors';
import {
    actions as paymentActions,
    api as paymentApi,
    selectors as paymentSelectors,
} from '@elab/core/store/payment';
import * as configSelectors from '@elab/core/store/config/selectors';
import cartActions from '@elab/core/store/cart/actions';
import cartSelectors from '@elab/core/store/cart/selectors';
import cartTypes from '@elab/core/store/cart/types';
import dynaForms from '@elab/core/utils/dynaForms';
import analytics from '@elab/core/utils/analytics';
import logger from '@elab/core/utils/logger';
import CONSTANTS from '@elab/core/utils/constants';
import { getUserId } from '@elab/core/utils/user/sagaHelpers';

import { errorTypes } from './constants';
import getActions from './actions';
import getSelectors from './selectors';
import getTypes from './types';
import { api } from './api';

export function* checkCardBalance(cardNumber) {
    const cardInfo = yield call(api.checkCardBalance, cardNumber);
    return {
        giftCard: cardInfo,
    };
}

const giftCardTogglerName = 'active';

export function* createGiftCardTransaction(requestBody) {
    const cartGuid = yield select(state => cartSelectors.getGuid(state));
    const userId = yield select(userSelectors.getUserId);
    const cardInfo = yield call(
        cartApi.applyDynamicGiftCard,
        userId,
        cartGuid,
        CONSTANTS.giftcardApiUrl,
        requestBody,
    );
    return cardInfo;
}

export function* createVoucherTransaction(submitUrl, cardNumber) {
    const userId = yield select(userSelectors.getUserId);
    const cartGuid = yield select(state => cartSelectors.getGuid(state));

    yield call(cartApi.applyGiftcardOrVoucher, userId, cartGuid, submitUrl, cardNumber);
}

export function* doSubmit(body, { actions, selectors }, submitUrl) {
    let cardInfo = {};
    let requestBody = {};
    let giftCardFields = [];
    const { giftCardNumber, voucherCode } = body;
    const currentCard = giftCardNumber || voucherCode;
    const cardNumber = (currentCard.value || '').trim();
    const cardInputFieldName = currentCard.name;
    const isPayment = yield select(selectors.getIsPayment);
    const isVoucher = yield select(selectors.getIsVoucher);
    const isCouponValidationEnabled = yield select(selectors.getCouponValidation);
    const couponValidationPattern = yield select(selectors.getValidationPattern);
    const couponErrorText = yield select(selectors.getCouponErrorText);
    const isBalanceCheck = yield select(selectors.getIsBalanceCheck);
    const availableGiftCardMethods = yield select(paymentSelectors.getGiftCardMethods);
    const giftCardTypeIndex = yield select(selectors.getGiftCardType);

    if (isPayment) {
        requestBody.cardType = availableGiftCardMethods[giftCardTypeIndex].name;

        giftCardFields = [
            'giftCardNumber',
            ...availableGiftCardMethods[giftCardTypeIndex].extraInputFields.map(el => el.name),
        ];

        for (const key in body) {
            if (giftCardFields.includes(key)) requestBody[key] = body[key].value;
        }
    }

    if (isBalanceCheck) {
        yield spawn(analytics.handleCheckGiftCardBalance, 1);
    }

    try {
        if (!cardNumber) {
            throw errorTypes.card;
        }

        if (isPayment) {
            cardInfo = yield call(createGiftCardTransaction, requestBody);
        } else if (isVoucher) {
            // eslint-disable-next-line elab-rules/detect-non-literal-regexp
            const patternValidator = new RegExp(couponValidationPattern);
            if (isCouponValidationEnabled && patternValidator.test(cardNumber)) {
                yield delay(400);
                yield call(
                    handleErrors,
                    { message: couponErrorText || '' },
                    cardInputFieldName,
                    cardInfo,
                );
            } else {
                yield call(createVoucherTransaction, submitUrl, cardNumber);
                yield put(cartActions.loadCart());
            }
        } else {
            cardInfo = yield call(checkCardBalance, cardNumber);
        }

        yield put(uiActions.openDialog({ name: 'giftcard-is-valid-dialog' }));

        if (!(isVoucher || isBalanceCheck) && !cardInfo.giftCard.balance.value) {
            cardInfo = {};
            throw errorTypes.other;
        }

        if (isPayment) {
            for (const field of giftCardFields) {
                yield put(actions.setDataFieldValue(field, '', true));
            }
            yield spawn(analytics.handleGiftCardPayment, cardInfo.giftCard.cardNumber);
            yield put(cartActions.loadCart());
            yield call(setLoyaltyPointsPayMethod);
        }

        yield put(actions.setDataFieldValue(cardInputFieldName, '', true));

        if (isBalanceCheck) {
            yield spawn(analytics.handleCheckGiftCardBalance, 2);
        }
    } catch (e) {
        yield call(handleErrors, e, cardInputFieldName, cardInfo);
    } finally {
        yield put(actions.setCardInfo(cardInfo));
    }
}

export function* setLoyaltyPointsPayMethod() {
    const shouldUpdatePWPPoints = yield select(configSelectors.getShouldUpdatePWPPoints);
    if (shouldUpdatePWPPoints) {
        const payWithLoyaltyPointsMethod = yield select(
            paymentSelectors.getPayWithLoyaltyPointsMethod,
        );
        const userId = yield call(getUserId);
        let cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);
        if (payWithLoyaltyPointsMethod) {
            const payWithLoyaltyPointsMethodExtraInfo = yield call(
                paymentApi.getPayWithLoyaltyPointsMethodExtraInfo,
                userId,
                cartId,
            );
            yield put(
                paymentActions.setPayWithLoyaltyPointsMethodExtraInfo(
                    payWithLoyaltyPointsMethodExtraInfo,
                ),
            );
        }
    }
}

export function* handleErrors(e, cardInputFieldName) {
    const { actions, selectors } = yield getContext('ctx');
    const scrollToError = yield select(selectors.getScrollToError);
    let errorText = '';
    const { type, message = '' } = (e.json && e.json.errors[0]) || {};
    if (e === errorTypes.card) {
        errorText = yield select(selectors.getErrorText, e);
    } else {
        errorText = e.message;
    }

    if (type) {
        yield put(actions.setDataFieldTypeError(cardInputFieldName, type));
    }

    yield put(actions.setDataFieldError(cardInputFieldName, errorText || message));

    if (scrollToError) {
        yield put(uiActions.scrollToCenter());
    }
}

export function* deleteGiftCard(cardNumber, cartGuid, userId) {
    const { actions, selectors } = yield getContext('ctx');
    const isRemoveAllGiftCard = yield select(configSelectors.isRemoveAllGiftCard);
    const giftCardToBeDeleted = yield select(selectors.getGiftCardCodeToBeDeleted);

    if (giftCardToBeDeleted) {
        yield put(actions.setGiftCardCodeToBeDeleted(''));
        yield put(uiActions.closeDialog());
    }

    if (isRemoveAllGiftCard) {
        yield call(api.removeGiftCardTransactions, cartGuid, userId);
    } else {
        const cardCode = giftCardToBeDeleted || cardNumber;
        yield call(api.removeGiftCardTransaction, cartGuid, userId, cardCode);
    }
}

export function* deleteItem({ payload }) {
    const { selectors } = yield getContext('ctx');
    const cartGuid = yield select(cartSelectors.getGuid);
    const userId = yield select(userSelectors.getUserId);
    const isVoucher = yield select(selectors.getIsVoucher);

    try {
        if (isVoucher) {
            yield call(
                cartApi.removeGiftcardOrVoucher,
                userId,
                cartGuid,
                payload,
                CONSTANTS.voucherApiUrl,
            );
        } else {
            yield call(deleteGiftCard, payload, cartGuid, userId);
            yield call(setLoyaltyPointsPayMethod);
        }

        yield put(cartActions.loadCart());
        let isCartLoading = true;
        while (isCartLoading) {
            yield take(cartTypes.SET_LOADING);
            isCartLoading = yield select(cartSelectors.isLoading);
        }
        yield put(uiActions.scrollToTop({ target: 'e2-gift-card-form' }));
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* removeAllGiftCards() {
    const giftCardTransactions = yield select(cartSelectors.getGiftCardTransactions);
    if (!giftCardTransactions.length) return;

    const cartGuid = yield select(cartSelectors.getGuid);
    const userId = yield select(userSelectors.getUserId);

    try {
        yield call(api.removeGiftCardTransactions, cartGuid, userId);
        yield put(cartActions.loadCart());
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* togglersChange({ payload }) {
    if (payload.name === giftCardTogglerName && !payload.value) {
        yield call(removeAllGiftCards);
    }
}

export function* afterInitialize() {
    const { actions } = yield getContext('ctx');
    const isCartLoading = yield select(cartSelectors.isLoading);
    const cardData = yield select(cartSelectors.getData);
    if (isCartLoading || !cardData.giftCardTransactions) {
        yield take(cartTypes.SET_CART_DATA);
    }

    const giftCardTransactions = yield select(cartSelectors.getGiftCardTransactions);
    const isActiveOpened = !!giftCardTransactions.length;
    yield put(
        actions.toggleFieldValue({
            name: giftCardTogglerName,
            value: isActiveOpened,
        }),
    );

    let params = {};
    const giftCardFormConfig = yield select(configSelectors.getGiftCardFormConfig);
    if (giftCardFormConfig.giftCardAmountForm) {
        try {
            const uri = yield call(
                dynaFormsApi.getFormMetadata,
                giftCardFormConfig.giftCardAmountForm,
            );
            const { steps = [] } = yield call(dynaFormsApi.loadMetadata, uri);
            const { fields = [] } = steps[0] || {};
            params = fields[0].constraints ? fields[0].constraints[0].parameters : {};
        } catch (e) {
            /* Nothing */
        }
    }

    yield put(actions.setValidationParams(params));
}

export function* setProperties({ payload }) {
    const { actions } = yield getContext('ctx');
    if (payload.isBalanceCheck) {
        yield put(actions.setIsBalanceCheck(true));
    }
}

export function* validateGiftCardAmount(payload) {
    const { selectors } = yield getContext('ctx');

    const { cardBalance, amountToUse } = payload;
    const cartTotal = payload.cartTotal.value;
    const params = yield select(selectors.getValidationParams);

    return (
        validation.pattern(amountToUse, '', params) &&
        amountToUse !== '' &&
        amountToUse <= cardBalance &&
        amountToUse <= cartTotal
    );
}

export function* submitAmountChange(cardNumber, amountToUse) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(cartSelectors.getCartId);

    try {
        yield call(api.updateGiftCardAmount, userId, cartId, cardNumber, amountToUse);
    } catch (e) {
        throw new Error(`Failed updating gift card amount: ${e})`);
    }
}

export function* updateGiftCardAmount({ payload }) {
    const { selectors, actions } = yield getContext('ctx');

    const validInput = yield call(validateGiftCardAmount, payload);
    let cardsWithError = yield select(selectors.getCardsWithError);
    const currentCardHasError = yield select(selectors.getCardHasError, payload.cardNumber);
    if (!validInput) {
        if (!currentCardHasError) {
            yield put(actions.setCardsWithError([...cardsWithError, payload.cardNumber]));
        }
    } else {
        if (currentCardHasError && cardsWithError.length > 0) {
            cardsWithError = cardsWithError.filter(cardNum => cardNum !== payload.cardNumber);

            yield put(actions.setCardsWithError(cardsWithError));
        }

        try {
            yield call(submitAmountChange, payload.cardNumber, payload.amountToUse);
            yield call(setLoyaltyPointsPayMethod);
            yield put(cartActions.loadCart());
        } catch {
            yield put(actions.setCardsWithError([...cardsWithError, payload.cardNumber]));
        }
    }
}

export function* askRemovalConfirmation({ payload }) {
    const { actions } = yield getContext('ctx');

    yield put(actions.setGiftCardCodeToBeDeleted(payload.number));
    yield put(uiActions.openDialog({ name: payload.dialogName }));
}

export function* setCardType({ payload: { name, value } }) {
    if (name === 'giftCardNumber') {
        const availableGiftCards = yield select(paymentSelectors.getGiftCardMethods);

        const index = availableGiftCards.findIndex(
            cardType =>
                cardType.length === value.length &&
                cardType.regexes.some(regEx => RegExp(regEx).test(value)),
        );
        const type = index === -1 ? 0 : index;
        yield put(getActions('gift-card').setGiftCardType(type));
    }
}

export function* focusInput() {
    const { selectors } = yield getContext('ctx');
    const isBalanceCheck = yield select(selectors.getIsBalanceCheck);
    if (isBalanceCheck) {
        yield spawn(analytics.handleCheckGiftCardBalance, 0);
    }
}

// FormId is used to create pure dynaform as in MRN for vouchers form
export default (storeName, formId) => () => {
    const actions = getActions(storeName);
    const selectors = getSelectors(storeName);
    const types = getTypes(storeName);
    const ctx = { actions, selectors, types, storeName };
    return [
        ...dynaForms.createSagaFor(
            ctx,
            formId ? dynaFormsApi.getFormMetadata(formId) : require('./metadata.js').default,
            doSubmit,
        ),
        takeEvery(types.DELETE_ITEM, deleteItem),
        takeEvery(types.TOGGLE_FIELD_VALUE, togglersChange),
        takeEvery(types.AFTER_INITIALIZE, afterInitialize),
        takeEvery(types.SET_PROPERTIES, setProperties),
        takeEvery(types.UPDATE_GIFT_CARD_AMOUNT, updateGiftCardAmount),
        takeEvery(types.ASK_REMOVAL_CONFIRMATION, askRemovalConfirmation),
        takeEvery(types.FOCUS_FIELD, focusInput),
        takeLatest(types.SET_DATA_FIELD_VALUE, setCardType),
    ];
};
